import React from 'react'
import { Card, Popup, Stack, Typography } from '@prometheus/components'

interface PlanetCyclePopupProps {
  trigger: React.ReactNode
  disablePlanetTokens: boolean
  /** Years the planet has been holding, when it is stuck on resources. */
  holdingYears?: number
  position?: string
}

/**
 * What the planet's cycle readout means.
 *
 * The readout carries a clock that changes what it is measuring, which nobody
 * can be expected to infer: while a planet grows, one number counts down to
 * both the mine payout and the level attempt, because they land on the same
 * year. Once it stalls, progress freezes while the mines carry on — so the
 * fraction is replaced by a lock and a live yield countdown.
 */
export const PlanetCyclePopup = ({
  trigger,
  disablePlanetTokens,
  holdingYears = 0,
  position = 'bottom right',
}: PlanetCyclePopupProps) => (
  <Popup position={position as any} hoverable trigger={trigger}>
    <Card padding="sm">
      <Stack space="sm">
        <Typography variant="h4" retro>Growth &amp; mining</Typography>

        {disablePlanetTokens ? (
          <>
            <Typography variant="body2">
              <b>2/2.5</b> — years into this planet&apos;s cycle. At the end of one the mines pay
              out and the planet tries for its next level. Both land on the same year.
            </Typography>
            <Typography variant="body2">
              <b>🔒</b> — the cycle is finished but the next level&apos;s resources are short, so
              progress is held where it is and the planet retries every year. Give it what the
              level costs and it takes it straight away; nothing has been lost in the meantime.
            </Typography>
            <Typography variant="body2">
              <b>⛏2y</b> — years until the mines next pay out. They keep producing while the
              planet waits, which is why this carries on counting while the cycle is locked.
            </Typography>
            {holdingYears > 1 && (
              <Typography variant="body2" color="warning">
                Waiting {holdingYears} years so far. Check this planet&apos;s level rows for what
                it needs — its own mines may not produce that resource at all.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2">
            The number is the planet&apos;s dice tile. Its mines pay out and it tries for its next
            level on any year that number is rolled.
          </Typography>
        )}
      </Stack>
    </Card>
  </Popup>
)

export default PlanetCyclePopup
